import React, { useState, useEffect } from 'react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { 
  Eye, Baby, Activity, ChevronDown, ChevronRight, 
  AlertCircle, CheckCircle, Clock, Calendar, Stethoscope,
  FileText, Users, Star, BookOpen, Heart, Target
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Componente Card stilizzato
const ProtocolCard = ({ title, icon: Icon, children, color = "#2A9FCB", defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-4">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        data-testid={`protocol-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
          <h3 className="font-semibold text-gray-800">{title}</h3>
        </div>
        {isOpen ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-4">
          {children}
        </div>
      )}
    </div>
  );
};

// Badge per severità
const SeverityBadge = ({ level, text }) => {
  const colors = {
    mild: 'bg-green-100 text-green-700',
    moderate: 'bg-amber-100 text-amber-700',
    severe: 'bg-red-100 text-red-700'
  };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[level] || 'bg-gray-100 text-gray-700'}`}>
      {text}
    </span>
  );
};

export const ProtocolliStrabismo = () => {
  const [treatmentPlan, setTreatmentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('screening');
  
  useEffect(() => {
    fetchTreatmentPlan();
  }, []);
  
  const fetchTreatmentPlan = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/prevention/treatment-plan/strabismo_ambliopia`);
      setTreatmentPlan(response.data);
    } catch (err) {
      console.error('Error fetching treatment plan:', err);
      setError('Impossibile caricare il protocollo. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };
  
  const tabs = [
    { id: 'screening', label: 'Screening Pediatrico', icon: Baby },
    { id: 'ambliopia', label: 'Trattamento Ambliopia', icon: Eye },
    { id: 'strabismo', label: 'Trattamento Strabismo', icon: Target },
    { id: 'lifestyle', label: 'Stile di Vita', icon: Heart },
    { id: 'monitoring', label: 'Monitoraggio', icon: Clock }
  ];
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50/50 to-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2A9FCB] border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600">Caricamento protocolli...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50/50 to-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Errore</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={fetchTreatmentPlan}
              className="px-6 py-2 bg-[#2A9FCB] text-white rounded-lg hover:bg-[#0B7A70] transition-colors"
            >
              Riprova
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  const plan = treatmentPlan?.plan;
  const prevention = plan?.prevention_protocol;
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/50 to-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-28 pb-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#2A9FCB]/10 text-[#2A9FCB] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Eye className="w-4 h-4" />
            <span>Protocolli Clinici PRIME VISION</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Protocolli Strabismo e Ambliopia
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Linee guida evidence-based per la diagnosi precoce e il trattamento dello strabismo e dell'ambliopia in età pediatrica
          </p>
        </div>
      </section>
      
      {/* Risk Factors Section */}
      <section className="py-8 px-4 bg-white border-y border-gray-100">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            Fattori di Rischio
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Non modificabili */}
            <div className="bg-red-50 rounded-xl p-5">
              <h3 className="font-semibold text-red-800 mb-4">Non Modificabili</h3>
              <ul className="space-y-3">
                {plan?.risk_factors?.non_modifiable && Object.entries(plan.risk_factors.non_modifiable).map(([key, value]) => (
                  <li key={key} className="flex items-start gap-3">
                    <span className="px-2 py-1 bg-red-200 text-red-800 rounded text-xs font-bold whitespace-nowrap">
                      +{value.increase_percent}%
                    </span>
                    <span className="text-red-900 text-sm">{value.description}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Clinici */}
            <div className="bg-amber-50 rounded-xl p-5">
              <h3 className="font-semibold text-amber-800 mb-4">Segni Clinici</h3>
              <ul className="space-y-3">
                {plan?.risk_factors?.clinical && Object.entries(plan.risk_factors.clinical).map(([key, value]) => (
                  <li key={key} className="flex items-start gap-3">
                    <span className="px-2 py-1 bg-amber-200 text-amber-800 rounded text-xs font-bold whitespace-nowrap">
                      +{value.increase_percent}%
                    </span>
                    <span className="text-amber-900 text-sm">{value.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* Tabs Navigation */}
      <section className="py-6 px-4 sticky top-24 bg-white/95 backdrop-blur-md z-40 border-b border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg whitespace-nowrap transition-all ${
                  activeTab === tab.id 
                    ? 'bg-[#2A9FCB] text-white shadow-md' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="font-medium text-sm">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Content Section */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          
          {/* Screening Pediatrico */}
          {activeTab === 'screening' && prevention?.screening_pediatrico && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
                <Baby className="w-5 h-5 text-[#2A9FCB]" />
                Screening Pediatrico per Età
              </h2>
              
              {prevention.screening_pediatrico.map((screening, idx) => (
                <ProtocolCard 
                  key={idx} 
                  title={screening.age} 
                  icon={Calendar}
                  color={screening.critical ? '#DC2626' : '#2A9FCB'}
                  defaultOpen={idx === 0}
                >
                  {screening.critical && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                      <p className="text-red-800 font-medium text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {screening.critical}
                      </p>
                    </div>
                  )}
                  
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">Esami Consigliati</h4>
                    <ul className="space-y-2">
                      {screening.exam.map((esame, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{esame}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                    <Stethoscope className="w-4 h-4" />
                    <span><strong>Chi esegue:</strong> {screening.who}</span>
                  </div>
                  
                  {screening.note && (
                    <p className="mt-3 text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
                      ℹ️ {screening.note}
                    </p>
                  )}
                </ProtocolCard>
              ))}
            </div>
          )}
          
          {/* Trattamento Ambliopia */}
          {activeTab === 'ambliopia' && prevention?.amblyopia_treatment && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#2A9FCB]" />
                Protocollo Trattamento Ambliopia
              </h2>
              
              {/* Step 1 - Correzione Ottica */}
              {prevention.amblyopia_treatment.step_1 && (
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">1</div>
                    <h3 className="font-semibold text-blue-900">{prevention.amblyopia_treatment.step_1.name}</h3>
                  </div>
                  <p className="text-blue-800 mb-3">{prevention.amblyopia_treatment.step_1.description}</p>
                  <p className="text-blue-700 text-sm mb-2"><strong>Dettagli:</strong> {prevention.amblyopia_treatment.step_1.details}</p>
                  <p className="text-blue-700 text-sm mb-2"><strong>Durata:</strong> {prevention.amblyopia_treatment.step_1.duration}</p>
                  <div className="bg-blue-100 rounded-lg p-3 mt-4">
                    <p className="text-blue-800 text-sm">📊 <strong>Evidenza:</strong> {prevention.amblyopia_treatment.step_1.evidence}</p>
                  </div>
                </div>
              )}
              
              {/* Step 2 - Patching */}
              {prevention.amblyopia_treatment.step_2_patching && (
                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold">2A</div>
                    <h3 className="font-semibold text-purple-900">{prevention.amblyopia_treatment.step_2_patching.name}</h3>
                  </div>
                  <p className="text-purple-700 mb-4">{prevention.amblyopia_treatment.step_2_patching.indication}</p>
                  
                  <h4 className="font-medium text-purple-800 mb-3">Protocolli per Severità:</h4>
                  <div className="grid gap-3 mb-4">
                    {prevention.amblyopia_treatment.step_2_patching.protocols?.map((protocol, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-3 flex items-center justify-between">
                        <span className="text-purple-900">{protocol.severity}</span>
                        <SeverityBadge 
                          level={idx === 0 ? 'mild' : idx === 1 ? 'moderate' : 'severe'}
                          text={protocol.hours}
                        />
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-purple-100 rounded-lg p-3 mb-3">
                    <p className="text-purple-800 text-sm">🎮 <strong>Durante:</strong> {prevention.amblyopia_treatment.step_2_patching.activities}</p>
                  </div>
                  
                  <h4 className="font-medium text-purple-800 mb-2">Consigli Pratici:</h4>
                  <ul className="space-y-2">
                    {prevention.amblyopia_treatment.step_2_patching.tips?.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-purple-700 text-sm">
                        <Star className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Step 2 - Atropina */}
              {prevention.amblyopia_treatment.step_2_atropine && (
                <div className="bg-sky-50 rounded-xl p-6 border border-sky-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-sky-500 text-white rounded-full flex items-center justify-center font-bold">2B</div>
                    <h3 className="font-semibold text-sky-900">{prevention.amblyopia_treatment.step_2_atropine.name}</h3>
                  </div>
                  <p className="text-sky-700 mb-3"><strong>Indicazione:</strong> {prevention.amblyopia_treatment.step_2_atropine.indication}</p>
                  <p className="text-sky-700 mb-3"><strong>Protocollo:</strong> {prevention.amblyopia_treatment.step_2_atropine.protocol}</p>
                  <p className="text-sky-700 mb-3"><strong>Meccanismo:</strong> {prevention.amblyopia_treatment.step_2_atropine.mechanism}</p>
                  <div className="bg-sky-100 rounded-lg p-3">
                    <p className="text-sky-800 text-sm">✅ <strong>Vantaggio:</strong> {prevention.amblyopia_treatment.step_2_atropine.advantage}</p>
                    <p className="text-sky-800 text-sm mt-2">📚 <strong>Evidenza:</strong> {prevention.amblyopia_treatment.step_2_atropine.evidence}</p>
                  </div>
                </div>
              )}
              
              {/* Terapie Adiuvanti */}
              {prevention.amblyopia_treatment.adjunctive && (
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <h3 className="font-semibold text-gray-800 mb-4">Terapie Adiuvanti</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {prevention.amblyopia_treatment.adjunctive.map((adj, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-4 border border-gray-100">
                        <h4 className="font-medium text-gray-800 mb-2">{adj.name}</h4>
                        <p className="text-sm text-gray-600 mb-2">{adj.indication}</p>
                        {adj.activities && <p className="text-sm text-gray-500">{adj.activities}</p>}
                        {adj.examples && <p className="text-sm text-gray-500 italic">{adj.examples}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Trattamento Strabismo */}
          {activeTab === 'strabismo' && prevention?.strabismus_treatment && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
                <Target className="w-5 h-5 text-[#2A9FCB]" />
                Protocollo Trattamento Strabismo
              </h2>
              
              {/* Terapia Conservativa */}
              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <h3 className="font-semibold text-green-900 mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Terapia Conservativa
                </h3>
                <div className="space-y-4">
                  {prevention.strabismus_treatment.conservative?.map((treatment, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 border border-green-100">
                      <h4 className="font-medium text-green-800 mb-2">{treatment.name}</h4>
                      <p className="text-green-700 text-sm mb-2"><strong>Indicazione:</strong> {treatment.indication}</p>
                      {treatment.details && <p className="text-green-600 text-sm">{treatment.details}</p>}
                      {treatment.types && <p className="text-green-600 text-sm">Tipi: {treatment.types}</p>}
                      {treatment.muscle && <p className="text-green-600 text-sm">{treatment.muscle}</p>}
                      {treatment.duration && <p className="text-green-600 text-sm">Durata: {treatment.duration}</p>}
                      {treatment.success && (
                        <div className="mt-2 bg-green-100 rounded p-2">
                          <p className="text-green-800 text-sm">✅ {treatment.success}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Chirurgia */}
              {prevention.strabismus_treatment.surgical && (
                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <h3 className="font-semibold text-red-900 mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Chirurgia
                  </h3>
                  <p className="text-red-700 mb-3"><strong>Indicazione:</strong> {prevention.strabismus_treatment.surgical.indication}</p>
                  <p className="text-red-700 mb-3"><strong>Timing:</strong> {prevention.strabismus_treatment.surgical.timing}</p>
                  
                  <h4 className="font-medium text-red-800 mb-2">Procedure:</h4>
                  <ul className="space-y-2 mb-4">
                    {prevention.strabismus_treatment.surgical.procedures?.map((proc, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-red-700">
                        <CheckCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <span>{proc}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="bg-red-100 rounded-lg p-3">
                    <p className="text-red-800 text-sm">🎯 <strong>Obiettivo:</strong> {prevention.strabismus_treatment.surgical.goal}</p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Stile di Vita */}
          {activeTab === 'lifestyle' && prevention?.lifestyle_amblyopia && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#2A9FCB]" />
                Consigli per lo Stile di Vita
              </h2>
              
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <div className="grid gap-3">
                  {prevention.lifestyle_amblyopia.map((advice, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 flex items-start gap-3 border border-pink-100">
                      <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-pink-600 font-bold text-sm">{idx + 1}</span>
                      </div>
                      <p className="text-gray-700">{advice}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Monitoraggio */}
          {activeTab === 'monitoring' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#2A9FCB]" />
                Monitoraggio e Follow-up
              </h2>
              
              {/* Timeline Monitoraggio */}
              {prevention?.monitoring && (
                <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                  <h3 className="font-semibold text-indigo-900 mb-4">Tempistiche di Controllo</h3>
                  <div className="space-y-4">
                    {Object.entries(prevention.monitoring).map(([key, value], idx) => (
                      <div key={key} className="flex items-center gap-4 bg-white rounded-lg p-4 border border-indigo-100">
                        <div className="w-10 h-10 bg-indigo-500 text-white rounded-full flex items-center justify-center">
                          <Clock className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-medium text-indigo-800 capitalize">
                            {key.replace(/_/g, ' ')}
                          </h4>
                          <p className="text-indigo-600 text-sm">{value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Prognosi */}
              {prevention?.prognosis && (
                <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
                  <h3 className="font-semibold text-amber-900 mb-4">Prognosi per Età di Inizio Trattamento</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {Object.entries(prevention.prognosis).map(([key, value], idx) => {
                      const colors = {
                        best: 'bg-green-100 border-green-200 text-green-800',
                        good: 'bg-blue-100 border-blue-200 text-blue-800',
                        moderate: 'bg-amber-100 border-amber-200 text-amber-800',
                        limited: 'bg-red-100 border-red-200 text-red-800'
                      };
                      return (
                        <div key={key} className={`rounded-lg p-4 border ${colors[key] || 'bg-gray-100'}`}>
                          <h4 className="font-medium capitalize mb-1">{key.replace(/_/g, ' ')}</h4>
                          <p className="text-sm">{value}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-12 px-4 bg-gradient-to-r from-[#2A9FCB] to-[#0B7A70]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Hai bisogno di una consulenza?</h2>
          <p className="text-sky-100 mb-6">
            Il nostro team di specialisti in strabismo e oftalmologia pediatrica è a tua disposizione
          </p>
          <a 
            href="/booking" 
            className="inline-flex items-center gap-2 bg-white text-[#2A9FCB] px-6 py-3 rounded-xl font-semibold hover:bg-sky-50 transition-colors"
            data-testid="book-appointment-btn"
          >
            <Calendar className="w-5 h-5" />
            Prenota una Visita
          </a>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default ProtocolliStrabismo;
